import _ from "lodash";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import UIModal from "UIComponents/UIModal";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import {
  Button,
  IconButton,
  Checkbox,
  TextInput,
  Tooltip,
  Modal,
} from "@toddle-edu/ds-web";
import { CloseOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";
import { getCriteriaSetLabels } from "Utils/GetCriteriaSetLabels";
import {
  getHeaderItems,
  getCategoryGradeBoundaryValues,
  checkAreScoresValid,
  copySLGradeScaleToHL,
  checkIsSameScale,
} from "../../modules/Utils";
import { getOrganizationPointSetQuery } from "ScoreCategories/modules/ScoreCategoriesQueries";
import { getOrganizationPointSetFromCache } from "ScoreCategories/modules/ScoreCategoriesGraphqlHelpers";
import {
  addCategoryGradeBoundary,
  updateCategoryGradeBoundary,
  getCategoryGradeBoundaryInitialValues,
} from "ScoreCategories/modules/ScoreCategoriesModule";
import classNames from "classnames";
import classes from "./CategoryGradeBoundary.scss";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX } from "Constants";
import { CATEGORY_SCORE } from "Constants/stringConstants";

const styles = {
  textInputStyle: {
    width: "76px",
  },
  modalBody: { padding: "16px 24px" },
};

const CategoryGradeBoundary = ({
  t,
  label,
  subjectType,
  gradeBoundaryId,
  hasSLWeight,
  hasHLWeight,
  curriculumProgramId,
  subjectLevelGenericTags,
  addCategoryGradeBoundary,
  updateCategoryGradeBoundary,
  setIsTemplateUpdated,
  mapCategoryWithGradeBoundary,
  closeModal,
  categoryGradeBoundaries,
  categoryGradeBoundaryValues,
  overallCategoryGradeBoundaryValues,
  updateCurrentCategoryGradeBoundaryValues,
  updateOverallCategoryGradeBoundaryValues,
  setToastMsg,
  isLoading,
  isViewMode: initialIsViewMode = false,
  portalType,
  organizationId,
  showEditBtn = false,
  isGradingPeriodUnlocked,
  showUpdatedDesign = false,
  isData,
}) => {
  const hasBothScales = hasSLWeight && hasHLWeight;
  const [isSameScale, setIsSameScale] = useState(false);
  const [errorsCount, setErrorsCount] = useState(0);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const [autoFocusKey, setAutoFocusKey] = useState("");
  const [isViewMode, setIsViewMode] = useState(initialIsViewMode);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const categoryGradeBoundaryInitialValues = getCategoryGradeBoundaryInitialValues(
    { subjectType }
  );

  useEffect(() => {
    if (!gradeBoundaryId) {
      updateCurrentCategoryGradeBoundaryValues(
        categoryGradeBoundaryInitialValues
      );
    }
  }, [gradeBoundaryId]);

  useEffect(() => {
    if (gradeBoundaryId) {
      setIsSameScale(
        checkIsSameScale({
          categoryGradeBoundaryValues,
          subjectLevelGenericTags,
        })
      );
    }
  }, [gradeBoundaryId, categoryGradeBoundaryValues]);

  useEffect(() => {
    return () => updateCurrentCategoryGradeBoundaryValues([]);
  }, []);

  useEffect(() => {
    if (_.isEmpty(categoryGradeBoundaryValues)) {
      if (!_.isEmpty(categoryGradeBoundaries)) {
        const updatedValues = getCategoryGradeBoundaryValues({
          categoryGradeBoundaries,
          categoryGradeBoundaryValues: categoryGradeBoundaryInitialValues,
          subjectType,
        });
        updateCurrentCategoryGradeBoundaryValues(updatedValues);
      } else if (_.get(overallCategoryGradeBoundaryValues, gradeBoundaryId)) {
        updateCurrentCategoryGradeBoundaryValues(
          _.cloneDeep(
            _.get(overallCategoryGradeBoundaryValues, gradeBoundaryId)
          )
        );
      }
    }
  }, [overallCategoryGradeBoundaryValues, categoryGradeBoundaries]);

  useEffect(() => {
    let saveBtnDisabled = false;
    _.forEach(categoryGradeBoundaryValues, item => {
      _.forEach(
        !_.isEmpty(subjectLevelGenericTags)
          ? subjectLevelGenericTags
          : [{ label: "Core" }],
        tag => {
          const tagLabel = _.get(tag, "label");
          const scoreRange = _.get(
            item,
            tagLabel === "Core" ? "SL" : tagLabel,
            {}
          );
          if (_.isEqual(tagLabel, "SL") && !hasSLWeight) return false;
          else if (_.isEqual(tagLabel, "HL") && !hasHLWeight) return false;
          else {
            if (
              _.isEmpty(_.get(scoreRange, "minValue")) ||
              _.isEmpty(_.get(scoreRange, "maxValue"))
            ) {
              saveBtnDisabled = true;
              return false;
            }
          }
        }
      );
    });

    setIsSaveBtnDisabled(saveBtnDisabled);
  }, [categoryGradeBoundaryValues, subjectLevelGenericTags, errorsCount]);

  const errors = checkAreScoresValid({
    t,
    categoryGradeBoundaryValues,
    hasSL: hasSLWeight,
    hasHL: hasHLWeight,
    noSLorHL: _.isEmpty(subjectLevelGenericTags),
    isSameScale,
  });

  useEffect(() => {
    if (!isSaveBtnDisabled) {
      setErrorsCount(_.size(errors));
    }

    return () => setErrorsCount(0);
  }, [isSaveBtnDisabled, errors]);

  const showTwoColumns = isSameScale || !hasSLWeight || !hasHLWeight;

  const subtextClass = classNames(classes.subtext, "text-body-1");
  const checkboxTextClass = classNames(classes.checkboxText, {
    ["text-label-2"]: !showUpdatedDesign,
    ["text-body"]: showUpdatedDesign,
  });
  const innerContainerClass = classNames({
    [classes.innerContainer]: true,
    [classes.twoColumns]: showTwoColumns,
  });

  const renderScoreRangeInput = (
    grade,
    index,
    scoreRange,
    tagLabel,
    isLastItem
  ) => {
    const minValue = _.get(scoreRange, "minValue", "");
    const maxValue = _.get(scoreRange, "maxValue", "");

    const handleValueChange = (valueKey, e) => {
      const value = _.get(e, "target.value", "");

      // allow only numbers and upto 2 decimals
      if (!DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX.test(value) && value) return;

      const updatedValues = _.cloneDeep(categoryGradeBoundaryValues);
      const maxValueKey = "maxValue";

      if (isSameScale) {
        _.set(_.get(updatedValues, [index]), ["HL", valueKey], value);
        _.set(_.get(updatedValues, [index]), ["SL", valueKey], value);
        if (index > 0 && valueKey === "minValue") {
          _.set(_.get(updatedValues, [index - 1]), ["HL", maxValueKey], value);
          _.set(_.get(updatedValues, [index - 1]), ["SL", maxValueKey], value);
        }
      } else {
        _.set(_.get(updatedValues, [index]), [tagLabel, valueKey], value);
        if (index > 0 && valueKey === "minValue") {
          _.set(
            _.get(updatedValues, [index - 1]),
            [tagLabel, maxValueKey],
            value
          );
        }
      }
      updateCurrentCategoryGradeBoundaryValues(updatedValues);
    };

    const minScoreKey = `${grade}_${tagLabel}_minScore`;
    const maxScoreKey = `${grade}_${tagLabel}_maxScore`;

    const { errorMsg: minScoreErrorMsg = "" } = _.get(errors, minScoreKey, {});
    const { errorMsg: maxScoreErrorMsg = "" } = _.get(errors, maxScoreKey, {});

    const minScoreHasError = errorsCount !== 0 && !_.isEmpty(minScoreErrorMsg);
    const maxScoreHasError = errorsCount !== 0 && !_.isEmpty(maxScoreErrorMsg);

    const groupedMinMaxScoreClass = classNames({
      [classes.groupedMinMaxScore]: !showUpdatedDesign,
      [classes.lastItem]: !showUpdatedDesign && isLastItem,
      ["text-body-1"]: !showUpdatedDesign,
      ["text-body"]: showUpdatedDesign,
      [classes.groupedMinMaxScoreV2]: showUpdatedDesign,
    });

    const lastRowMaxScoreGroupClasses = classNames({
      [classes.lastRowMaxScoreGroup]: !showUpdatedDesign,
      [classes.lastRowMaxScoreGroupV2]: showUpdatedDesign,
    });

    const orHigherTextClasses = classNames({
      [classes.orHigherText]: !showUpdatedDesign,
      [classes.orHigherTextV2]: showUpdatedDesign,
    });

    return (
      <div className={groupedMinMaxScoreClass}>
        <ConditionalWrapper
          condition={
            !isViewMode && errorsCount !== 0 && !_.isEmpty(minScoreErrorMsg)
          }
          wrapper={
            <Tooltip
              placement={"left"}
              tooltip={minScoreErrorMsg}
              showArrow={true}
              dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
            />
          }
          type="wrapperAsElement"
        >
          <TextInput
            style={styles.textInputStyle}
            value={minValue}
            onChange={e => handleValueChange("minValue", e)}
            disabled={isViewMode || _.isEqual(index, 0)}
            error={minScoreHasError}
            autoFocus={autoFocusKey === minScoreKey}
            onFocus={() => setAutoFocusKey(minScoreKey)}
            onBlur={() => setAutoFocusKey("")}
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
          />
        </ConditionalWrapper>

        {!isLastItem ? (
          <div className={orHigherTextClasses}>
            {t("academicSetup:percentage_or_higher")}
          </div>
        ) : (
          <div className={lastRowMaxScoreGroupClasses}>
            <span>-</span>
            <ConditionalWrapper
              condition={
                !isViewMode && errorsCount !== 0 && !_.isEmpty(maxScoreErrorMsg)
              }
              wrapper={
                <Tooltip
                  placement={"right"}
                  tooltip={maxScoreErrorMsg}
                  showArrow={true}
                  dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
                />
              }
              type="wrapperAsElement"
            >
              <TextInput
                style={styles.textInputStyle}
                value={maxValue}
                onChange={e => handleValueChange("maxValue", e)}
                error={maxScoreHasError}
                autoFocus={autoFocusKey === maxScoreKey}
                onFocus={() => setAutoFocusKey(maxScoreKey)}
                disabled={isViewMode}
                onBlur={() => setAutoFocusKey("")}
                dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
              />
            </ConditionalWrapper>
          </div>
        )}
      </div>
    );
  };

  const onClickCheckbox = e => {
    if (isViewMode) return;
    e.stopPropagation();
    if (!isSameScale) {
      const updatedCategoryGradeBoundaryValues = copySLGradeScaleToHL({
        categoryGradeBoundaryValues,
      });
      updateCurrentCategoryGradeBoundaryValues(
        updatedCategoryGradeBoundaryValues
      );
    }
    setIsSameScale(!isSameScale);
  };

  const onClickSave = async () => {
    if (_.size(errors) > 0) {
      setErrorsCount(_.size(errors));
      setToastMsg({
        msg: "toastMsgs:rectify_errors_with_count",
        locale_params: [
          { key: "count", value: _.size(errors), isPlainText: true },
        ],
        type: "warning",
        position: "toast-top-center",
      });
      return;
    }

    if (showUpdatedDesign) setShowFullScreenLoader(true);

    if (!gradeBoundaryId) {
      const response = await addCategoryGradeBoundary({
        categoryGradeBoundaryValues,
        subjectLevelGenericTags,
        curriculumProgramId,
        portalType,
      });
      const updatedValues = getCategoryGradeBoundaryValues({
        categoryGradeBoundaries: response,
        categoryGradeBoundaryValues: categoryGradeBoundaryInitialValues,
        subjectType,
      });
      const id = _.get(response, "id");

      if (id) {
        updateOverallCategoryGradeBoundaryValues({
          gradeBoundaryId: id,
          categoryGradeBoundaryValues: updatedValues,
        });
        mapCategoryWithGradeBoundary(id);
      }
    } else {
      await updateCategoryGradeBoundary({
        categoryGradeBoundaryValues,
        subjectLevelGenericTags,
        gradeBoundaryId,
        extraParams: {
          subType: "CATEGORY_GRADE_BOUNDARY",
        },
        portalType,
      });
      updateOverallCategoryGradeBoundaryValues({
        gradeBoundaryId,
        categoryGradeBoundaryValues,
      });
      setIsTemplateUpdated(true);
    }

    if (showUpdatedDesign) setShowFullScreenLoader(false);

    closeModal();
  };

  const criteriaLabels = getCriteriaSetLabels({
    organizationId,
    curriculumProgramId,
    criteriaTypes: [CATEGORY_SCORE],
  });

  const headerItems = getHeaderItems({
    t,
    isSameScale,
    subjectLevelGenericTags,
    hasSLWeight,
    hasHLWeight,
    criteriaLabels,
  });

  const checkboxContainerClass = classNames({
    [classes.checkboxContainer]: true,
    [classes.cursorPointer]: !isViewMode,
  });

  const getHeaderTitle = () => {
    if (isViewMode) {
      return t("academicSetup:category_grade_boundary_view_label", {
        label,
      });
    }
    return t("academicSetup:category_grade_boundary_creation_label", {
      label,
    });
  };

  const showEditButton = showEditBtn && isViewMode && isGradingPeriodUnlocked;

  const getActionElement = () => {
    return showEditButton ? (
      <Button
        onClick={() => setIsViewMode(false)}
        icon={<PencilLineOutlined />}
        variant="neutral"
        type="outlined"
        dsVersion="2.0"
        disabled={isLoading}
      >
        {t("common:edit")}
      </Button>
    ) : null;
  };

  const getModalBody = () => {
    const headerItemClass = classNames({
      [classes.headerItem]: !showUpdatedDesign,
      ["heading-6"]: !showUpdatedDesign,
      ["text-label"]: showUpdatedDesign,
      [classes.headerItemV2]: showUpdatedDesign,
    });

    const gradeLabelClasses = classNames({
      ["text-body-1"]: !showUpdatedDesign,
      ["text-body"]: showUpdatedDesign,
      [classes.gradeLabel]: showUpdatedDesign,
    });

    return !isData && isLoading ? (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    ) : (
      <>
        {hasBothScales && (
          <div className={checkboxContainerClass} onClick={onClickCheckbox}>
            <Checkbox isChecked={isSameScale} disabled={isViewMode} />
            <div className={checkboxTextClass}>
              {t("academicSetup:use_same_grade_boundary_for_sl_hl")}
            </div>
          </div>
        )}
        <div className={innerContainerClass}>
          {_.map(headerItems, item => (
            <div className={headerItemClass}>{item}</div>
          ))}
          {_.map(categoryGradeBoundaryValues, (item, index) => {
            const grade = _.get(item, "grade", "");
            const scoreRangeSL = _.get(item, "SL", "");
            const scoreRangeHL = _.get(item, "HL", "");
            const isLastItem =
              _.size(categoryGradeBoundaryInitialValues) - 1 === index;

            return (
              <React.Fragment key={grade}>
                <div className={gradeLabelClasses}>{grade}</div>
                {isSameScale || _.isEmpty(subjectLevelGenericTags) ? (
                  renderScoreRangeInput(
                    grade,
                    index,
                    scoreRangeSL,
                    "SL",
                    isLastItem
                  )
                ) : (
                  <React.Fragment>
                    {hasSLWeight &&
                      renderScoreRangeInput(
                        grade,
                        index,
                        scoreRangeSL,
                        "SL",
                        isLastItem
                      )}
                    {hasHLWeight &&
                      renderScoreRangeInput(
                        grade,
                        index,
                        scoreRangeHL,
                        "HL",
                        isLastItem
                      )}
                  </React.Fragment>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </>
    );
  };

  const getModalFooter = () => {
    return (
      <>
        <Button
          _legacy_size="large"
          size="medium"
          _legacy_variant="outlined-subtle"
          variant="neutral"
          type="outlined"
          onClick={closeModal}
          dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
        >
          {isViewMode ? t("common:close") : t("common:cancel")}
        </Button>
        {!isViewMode ? (
          <Button
            _legacy_size="large"
            size="medium"
            variant="progressive"
            onClick={onClickSave}
            disabled={isSaveBtnDisabled}
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
          >
            {t("common:save")}
          </Button>
        ) : null}
      </>
    );
  };

  if (showUpdatedDesign) {
    return (
      <>
        <Modal
          shouldCloseOnEsc={true}
          onClose={closeModal}
          dsVersion="2.0"
          width="60vw"
          height="65vh"
        >
          <Modal.Header
            title={getHeaderTitle()}
            actionElement={getActionElement()}
          />
          <Modal.Body isScrollable={true} style={styles.modalBody}>
            {getModalBody()}
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.footerContainerV2}>{getModalFooter()}</div>
          </Modal.Footer>
        </Modal>

        {showFullScreenLoader ? <FullScreenLoader /> : null}
      </>
    );
  }

  return (
    <UIModal
      isOpen={true}
      modalContent={classes.modalContent}
      overlayContainer={classes.overlayContainer}
    >
      <div className={classes.headerContainer}>
        <div className={classes.titleContainer}>
          <div className="heading-4">{getHeaderTitle()}</div>
        </div>
        <div className={classes.headerBtnContainer}>
          {showEditButton ? getActionElement() : null}
          <IconButton
            icon={<CloseOutlined variant="subtle" />}
            _legacy_variant="plain"
            variant="neutral"
            type="plain"
            onClick={closeModal}
          />
        </div>
      </div>
      <div className={classes.bodyContainer}>{getModalBody()}</div>
      <div className={classes.footerContainer}>{getModalFooter()}</div>
    </UIModal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { portalType } = ownProps;
  const organizationId = _.get(state, "login.userInfo.org_id");
  const curriculumProgramId = _.get(ownProps, "curriculumProgramId");
  const categoryId = _.get(ownProps, "categoryId");
  const gradeBoundaryId = _.get(ownProps, "gradeBoundaryId");
  const categoryGradeBoundaryValues = _.get(
    ownProps,
    "currentCategoryGradeBoundaryValues",
    []
  );
  const overallCategoryGradeBoundaryValues = _.get(
    ownProps,
    "overallCategoryGradeBoundaryValues",
    {}
  );

  return {
    categoryId,
    gradeBoundaryId,
    categoryGradeBoundaryValues,
    overallCategoryGradeBoundaryValues,
    organizationId,
    pointSetVariables: {
      id: organizationId,
      filters: {
        types: "GRADE_BOUNDARY",
        curriculumProgramIds: curriculumProgramId,
        associatedEntites: [
          { ids: categoryId, type: "ACADEMIC_CRITERIA_VALUE" },
        ],
      },
      portalType,
    },
  };
};

const mapActionCreators = {
  addCategoryGradeBoundary,
  updateCategoryGradeBoundary,
  setToastMsg,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getOrganizationPointSetQuery, {
    name: "getOrganizationPointSet",
    skip: ({ categoryId, gradeBoundaryId }) => !categoryId || !gradeBoundaryId,
    options: ({ pointSetVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: pointSetVariables,
    }),
    props({ getOrganizationPointSet, ownProps: { pointSetVariables } }) {
      const organizationPointSetData = getOrganizationPointSetFromCache({
        variables: pointSetVariables,
      });

      const organizationPointSet = _.get(
        organizationPointSetData,
        "pointAssessmentSets.edges",
        []
      );

      return {
        categoryGradeBoundaries: _.get(
          _.first(organizationPointSet),
          "node",
          {}
        ),
        organizationPointSetCount: _.get(
          organizationPointSetData,
          "pointAssessmentSets.totalCount",
          0
        ),
        isData: !_.isEmpty(organizationPointSetData),
        isLoading: _.includes([1, 2], getOrganizationPointSet.networkStatus),
      };
    },
  })
)(CategoryGradeBoundary);
